// 서명 양식을 **캔버스 위에 직접 그리는** 방식의 정본.
//
// ── 왜 만들었나 ───────────────────────────────────────────────────────────
// 종전에는 양식을 만들려면 한글/워드로 문서를 만들어 PDF 로 내보내고 업로드해야
// 했다(오너 결정 #3 "베이스 PDF 강제", #8 "작성 도구는 안 만든다"). 그 대가가
// 쌓여 오너가 셋을 불편으로 꼽았다(2026-09-22, 불편한 순서대로):
//
//   1. 매번 외부 도구로 문서를 만들어 PDF 로 내보내는 것
//   2. 문구 하나 고치려도 PDF 를 새로 만들어 다시 올려야 하는 것
//   3. 올린 PDF 위에 필드를 드래그로 찍는 것
//
// 처음엔 위에서 아래로 쌓는 '블록' 으로 갔는데, 오너가 원한 것은 **한글처럼
// 빈 종이 위에 표·텍스트·도형·사진을 놓고 속성 패널로 다듬는** 화면이었다
// (2026-09-22 "내가 생각한건 이런거였어" + 표 속성 패널 시안). 그래서 흐름형
// 블록을 걷어내고 **절대 좌표 객체**로 바꿨다.
//
// ── 좌표계는 필드와 같다 ──────────────────────────────────────────────────
// 객체 좌표는 `SignatureField` 와 **같은 캔버스 px**(폭 794 · 장 높이 1122 ·
// y 는 전역, pageIndex = floor(y / 1122))다. 디자이너가 필드와 객체를 한
// 오버레이에 그리고, 서버 렌더러가 같은 변환으로 PDF 를 만든다. 좌표계가 둘이면
// 표 칸 위에 만든 필드가 어긋난다.
//
// ── 기존 구조를 건드리지 않는다 ───────────────────────────────────────────
// 객체는 **저작 수단**일 뿐이고, 서버가 객체에서 PDF 를 만들어 기존
// `SignatureTemplate.pdfS3Key` 자리에 넣는다. 서명·필드 오버레이·PDF 합성·
// 고객 포털은 바뀌지 않는다. PDF 만 올리는 종전 양식도 그대로 돈다.
/** 디자이너 캔버스 한 장의 크기(px). A4 를 96dpi 로 본 값. */
export const LAYOUT_PAGE_W = 794;
export const LAYOUT_PAGE_H = 1122;
/**
 * 글자 가로 정렬 기본값 — **가운데**(오너 요청 2026-09-23: "텍스트 정렬은 기본적으로 가운데
 * 정렬이 되게, 모든 팔레트에서"). 표 칸·텍스트(서식용)·표 칸 입력 필드가 모두 이 값을 쓴다.
 * 필드 팔레트로 새로 놓는 필드는 만들 때 `align: 'center'` 를 명시한다 — 이미 발행된
 * 문서의 필드는 `align` 이 없으면 왼쪽으로 찍히므로(포털·현장앱·서버 stamp 의 역호환) 그
 * 기본값은 건드리지 않는다.
 */
export const DEFAULT_LAYOUT_TEXT_ALIGN = 'center';
export const DEFAULT_CELL_IMAGE_PADDING = 2;
/**
 * 칸 사진이 실제로 차지할 사각형(칸 기준 절대 좌표). **화면과 PDF 가 같은 함수**를
 * 쓴다. `cover` 는 칸보다 커질 수 있다 — 그리는 쪽이 칸(여백 안)으로 잘라야 한다.
 */
export function layoutCellImageBox(cell, natural, ref, align = 'center', valign = 'middle') {
    const pad = Math.max(0, Math.min(ref.padding ?? DEFAULT_CELL_IMAGE_PADDING, Math.min(cell.w, cell.h) / 2 - 1));
    const clip = {
        x: cell.x + pad,
        y: cell.y + pad,
        w: Math.max(1, cell.w - pad * 2),
        h: Math.max(1, cell.h - pad * 2),
    };
    const fit = ref.fit ?? 'contain';
    if (fit === 'stretch' || natural.w <= 0 || natural.h <= 0)
        return { box: { ...clip }, clip };
    const s = fit === 'cover'
        ? Math.max(clip.w / natural.w, clip.h / natural.h)
        : Math.min(clip.w / natural.w, clip.h / natural.h);
    const w = natural.w * s;
    const h = natural.h * s;
    const x = align === 'left' ? clip.x : align === 'right' ? clip.x + clip.w - w : clip.x + (clip.w - w) / 2;
    const y = valign === 'top'
        ? clip.y
        : valign === 'bottom'
            ? clip.y + clip.h - h
            : clip.y + (clip.h - h) / 2;
    return { box: { x, y, w, h }, clip };
}
/**
 * 칸 글자의 세로 여백(px). 칸이 글자 한 줄보다 겨우 크면 기본 여백(4)이 자리를 다
 * 먹어 위/가운데/아래가 똑같아 보였다(2026-09-23 오너 지적). 남는 높이의 절반까지만 쓴다.
 */
export function layoutCellTextPadX(cellW) {
    // 좁은 열(수량·No. 등)에서 여백 4px 가 글자 자리를 먹어 두 글자도 줄이 넘어갔다.
    return Math.min(4, Math.max(1, cellW * 0.06));
}
export function layoutCellTextPadY(cellH, fontSize) {
    const lineH = fontSize * 1.35;
    return Math.max(0, Math.min(4, (cellH - lineH) / 2));
}
/** 선의 끝점 — `line` 이 없는 옛 객체는 상자 대각선으로 본다. */
export function layoutLineEnds(o) {
    return o.line ?? { x1: o.x, y1: o.y, x2: o.x + o.w, y2: o.y + o.h };
}
/** 두 끝점으로 선 객체의 상자를 다시 잰다(선택·검사용). 가로선도 폭 1 은 갖는다. */
export function layoutLineWithEnds(o, line) {
    const x = Math.min(line.x1, line.x2);
    const y = Math.min(line.y1, line.y2);
    return {
        ...o,
        line,
        x: Math.round(x),
        y: Math.round(y),
        w: Math.max(1, Math.round(Math.abs(line.x2 - line.x1))),
        h: Math.max(1, Math.round(Math.abs(line.y2 - line.y1))),
    };
}
/** 새 선 — 기본은 가로 직선(오너: "기본으로 직선으로 나오게"). */
export function newLayoutLine(id, x, y, length = 200) {
    const base = {
        kind: 'shape',
        id,
        shape: 'line',
        x,
        y,
        w: length,
        h: 1,
        stroke: { ...DEFAULT_LAYOUT_BORDER },
        fill: null,
    };
    return layoutLineWithEnds(base, { x1: x, y1: y, x2: x + length, y2: y });
}
export const LAYOUT_OBJECT_LABELS = {
    table: '표',
    image: '사진',
    // 필드 팔레트의 '텍스트'(입력 칸)와 헷갈리지 않게(오너 요청 2026-09-23).
    text: '텍스트(서식용)',
    shape: '도형',
};
/** 장 구성이 없을 때의 기본 — 밑그림 장 수만큼 차례로. */
export function layoutDefaultPages(basePageCount, objects) {
    const n = Math.max(basePageCount, layoutPageCount(objects), 1);
    return Array.from({ length: n }, (_, i) => (i < basePageCount ? i : null));
}
/**
 * i 번째 장을 지운다 — 그 장의 객체는 사라지고, 아래 장 객체는 한 장 위로 올라온다.
 * 필드도 같은 규칙으로 옮겨야 하므로 페이지가 `shiftUpFromPage` 로 같이 처리한다.
 */
export function layoutDeletePage(layout, pageIndex, basePageCount) {
    const pages = layout.pages ?? layoutDefaultPages(basePageCount, layout.objects);
    if (pages.length <= 1 || pageIndex < 0 || pageIndex >= pages.length)
        return layout;
    const nextPages = pages.filter((_, i) => i !== pageIndex);
    const objects = layout.objects
        .filter((o) => layoutPageIndex(o) !== pageIndex)
        .map((o) => {
        if (layoutPageIndex(o) < pageIndex)
            return o;
        const dy = -LAYOUT_PAGE_H;
        if (o.kind === 'shape' && o.line) {
            return layoutLineWithEnds(o, {
                x1: o.line.x1,
                y1: o.line.y1 + dy,
                x2: o.line.x2,
                y2: o.line.y2 + dy,
            });
        }
        return { ...o, y: o.y + dy };
    });
    return { ...layout, pages: nextPages, objects };
}
/** 빈 장을 `at` 자리에 넣는다(`at === pages.length` 면 맨 뒤). 그 아래 객체는 한 장 내려간다. */
export function layoutInsertBlankPage(layout, at, basePageCount) {
    const pages = layout.pages ?? layoutDefaultPages(basePageCount, layout.objects);
    const pos = Math.max(0, Math.min(pages.length, at));
    const nextPages = [...pages.slice(0, pos), null, ...pages.slice(pos)];
    const objects = layout.objects.map((o) => {
        if (layoutPageIndex(o) < pos)
            return o;
        const dy = LAYOUT_PAGE_H;
        if (o.kind === 'shape' && o.line) {
            return layoutLineWithEnds(o, {
                x1: o.line.x1,
                y1: o.line.y1 + dy,
                x2: o.line.x2,
                y2: o.line.y2 + dy,
            });
        }
        return { ...o, y: o.y + dy };
    });
    return { ...layout, pages: nextPages, objects };
}
/** 표 칸에 쓸 수 있는 필드 종류 — 팔레트와 **같은 목록**이어야 한다. */
export const LAYOUT_CELL_FIELD_TYPES = [
    'text',
    'signature',
    'stamp',
    'date',
    'checkbox',
    'dropdown',
    'image',
];
export const MAX_LAYOUT_OBJECTS = 200;
export const MAX_LAYOUT_TABLE_ROWS = 100;
export const MAX_LAYOUT_TABLE_COLS = 20;
/** 칸·객체의 최소 크기(px). 이보다 작으면 글자가 안 들어가고 잡을 수도 없다. */
export const MIN_LAYOUT_CELL = 12;
export const DEFAULT_LAYOUT_BORDER = { width: 1, color: '#222222', style: 'solid' };
export const DEFAULT_LAYOUT_FONT_SIZE = 13;
// ── 만들기 ────────────────────────────────────────────────────────────────
/** 새 표 — 균등 격자. 화면이 캔버스 클릭 자리에 놓는다. */
export function newLayoutTable(id, x, y, rows, cols, w = 480, rowH = 32) {
    const colW = Math.round(w / cols);
    return {
        kind: 'table',
        id,
        x,
        y,
        w: colW * cols,
        h: rowH * rows,
        colWidths: Array.from({ length: cols }, () => colW),
        rowHeights: Array.from({ length: rows }, () => rowH),
        cells: Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ text: '' }))),
        border: { ...DEFAULT_LAYOUT_BORDER },
        fontSize: DEFAULT_LAYOUT_FONT_SIZE,
    };
}
/** 한 칸의 절대 사각형(병합 포함). 덮인 칸을 물으면 그 칸 자체 자리를 준다. */
export function layoutCellRect(t, r, c) {
    const cell = t.cells[r]?.[c] ?? {};
    const rs = cell.rowSpan ?? 1;
    const cs = cell.colSpan ?? 1;
    let x = t.x;
    for (let i = 0; i < c; i++)
        x += t.colWidths[i] ?? 0;
    let y = t.y;
    for (let i = 0; i < r; i++)
        y += t.rowHeights[i] ?? 0;
    let w = 0;
    for (let i = c; i < Math.min(c + cs, t.colWidths.length); i++)
        w += t.colWidths[i] ?? 0;
    let h = 0;
    for (let i = r; i < Math.min(r + rs, t.rowHeights.length); i++)
        h += t.rowHeights[i] ?? 0;
    return { x, y, w, h };
}
/** 객체들이 차지하는 장 수. 비어 있어도 한 장은 있다. */
export function layoutPageCount(objects) {
    let bottom = 0;
    for (const o of objects)
        bottom = Math.max(bottom, o.y + o.h);
    return Math.max(1, Math.ceil(bottom / LAYOUT_PAGE_H) || 1);
}
/** 객체가 놓인 장(0-based). 객체는 시작점이 있는 장에 속한다. */
export function layoutPageIndex(o) {
    return Math.max(0, Math.floor(o.y / LAYOUT_PAGE_H));
}
// ── 격자 편집 (모두 새 객체를 돌려준다 — 화면 상태를 직접 바꾸지 않는다) ──────
function cloneCells(cells) {
    return cells.map((row) => row.map((c) => ({ ...c })));
}
/** (r, c) 를 덮고 있는 주인 칸의 자리. 주인이거나 덮이지 않았으면 자기 자신. */
export function layoutMasterOf(t, r, c) {
    if (!t.cells[r]?.[c]?.covered)
        return { r, c };
    for (let rr = r; rr >= 0; rr--) {
        for (let cc = c; cc >= 0; cc--) {
            const m = t.cells[rr]?.[cc];
            if (!m || m.covered)
                continue;
            const rs = m.rowSpan ?? 1;
            const cs = m.colSpan ?? 1;
            if (rr + rs > r && cc + cs > c)
                return { r: rr, c: cc };
        }
    }
    return { r, c };
}
/**
 * 행 넣기. `at` 자리에 들어가고 그 자리부터 아래로 밀린다(`at === rows` 면 맨 아래).
 * 넣는 자리를 가로지르는 병합은 한 줄 늘어난다 — 엑셀과 같다.
 */
export function layoutInsertRow(t, at, height) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    const pos = Math.max(0, Math.min(rows, at));
    const h = height ?? t.rowHeights[Math.min(pos, rows - 1)] ?? 32;
    const cells = cloneCells(t.cells);
    const newRow = Array.from({ length: cols }, () => ({ text: '' }));
    // 위쪽 주인 칸의 병합이 pos 를 가로지르면 rowSpan +1, 새 줄의 그 칸은 덮인다.
    for (let r = 0; r < pos; r++) {
        for (let c = 0; c < cols; c++) {
            const m = cells[r][c];
            if (m.covered)
                continue;
            const rs = m.rowSpan ?? 1;
            const cs = m.colSpan ?? 1;
            if (r + rs > pos) {
                m.rowSpan = rs + 1;
                for (let cc = c; cc < Math.min(cols, c + cs); cc++)
                    newRow[cc] = { covered: true };
            }
        }
    }
    cells.splice(pos, 0, newRow);
    const rowHeights = [...t.rowHeights];
    rowHeights.splice(pos, 0, h);
    return { ...t, cells, rowHeights, h: sum(rowHeights) };
}
/** 열 넣기 — `layoutInsertRow` 의 세로 판. */
export function layoutInsertCol(t, at, width) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    const pos = Math.max(0, Math.min(cols, at));
    const w = width ?? t.colWidths[Math.min(pos, cols - 1)] ?? 80;
    const cells = cloneCells(t.cells);
    const fresh = Array.from({ length: rows }, () => ({ text: '' }));
    // 왼쪽 주인 칸의 병합이 pos 를 가로지르면 colSpan +1, 새 열의 그 줄들은 덮인다.
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < pos; c++) {
            const m = cells[r][c];
            if (m.covered)
                continue;
            const rs = m.rowSpan ?? 1;
            const cs = m.colSpan ?? 1;
            if (c + cs > pos) {
                m.colSpan = cs + 1;
                for (let rr = r; rr < Math.min(rows, r + rs); rr++)
                    fresh[rr] = { covered: true };
            }
        }
    }
    for (let r = 0; r < rows; r++)
        cells[r].splice(pos, 0, fresh[r]);
    const colWidths = [...t.colWidths];
    colWidths.splice(pos, 0, w);
    return { ...t, cells, colWidths, w: sum(colWidths) };
}
/** 행 지우기. 마지막 한 줄은 지우지 않는다(표를 지우는 것과 다르다). */
export function layoutDeleteRow(t, at) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    if (rows <= 1 || at < 0 || at >= rows)
        return t;
    const cells = cloneCells(t.cells);
    // 이 줄에 주인이 있고 아래로 병합돼 있으면 주인을 다음 줄로 내린다.
    for (let c = 0; c < cols; c++) {
        const m = cells[at][c];
        if (m.covered) {
            // 위에서 내려온 병합이 이 줄을 지나면 rowSpan -1.
            const { r: mr, c: mc } = layoutMasterOf({ ...t, cells }, at, c);
            if (mr < at && mc === c) {
                const master = cells[mr][mc];
                master.rowSpan = Math.max(1, (master.rowSpan ?? 1) - 1);
            }
            continue;
        }
        const rs = m.rowSpan ?? 1;
        if (rs > 1 && at + 1 < rows) {
            const { covered: _c, ...rest } = m;
            cells[at + 1][c] = { ...rest, rowSpan: rs - 1 };
            if (rs - 1 === 1)
                delete cells[at + 1][c].rowSpan;
        }
    }
    cells.splice(at, 1);
    const rowHeights = [...t.rowHeights];
    rowHeights.splice(at, 1);
    return normalizeSpans({ ...t, cells, rowHeights, h: sum(rowHeights) });
}
/** 열 지우기 — `layoutDeleteRow` 의 세로 판. */
export function layoutDeleteCol(t, at) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    if (cols <= 1 || at < 0 || at >= cols)
        return t;
    const cells = cloneCells(t.cells);
    for (let r = 0; r < rows; r++) {
        const m = cells[r][at];
        if (m.covered) {
            const { r: mr, c: mc } = layoutMasterOf({ ...t, cells }, r, at);
            if (mc < at && mr === r) {
                const master = cells[mr][mc];
                master.colSpan = Math.max(1, (master.colSpan ?? 1) - 1);
            }
            continue;
        }
        const cs = m.colSpan ?? 1;
        if (cs > 1 && at + 1 < cols) {
            const { covered: _c, ...rest } = m;
            cells[r][at + 1] = { ...rest, colSpan: cs - 1 };
            if (cs - 1 === 1)
                delete cells[r][at + 1].colSpan;
        }
    }
    for (const row of cells)
        row.splice(at, 1);
    const colWidths = [...t.colWidths];
    colWidths.splice(at, 1);
    return normalizeSpans({ ...t, cells, colWidths, w: sum(colWidths) });
}
/** span 이 격자 밖으로 나가지 않게 자르고, span 1 은 지운다. */
function normalizeSpans(t) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const m = t.cells[r][c];
            if (m.covered)
                continue;
            if (m.rowSpan !== undefined) {
                m.rowSpan = Math.min(m.rowSpan, rows - r);
                if (m.rowSpan <= 1)
                    delete m.rowSpan;
            }
            if (m.colSpan !== undefined) {
                m.colSpan = Math.min(m.colSpan, cols - c);
                if (m.colSpan <= 1)
                    delete m.colSpan;
            }
        }
    }
    return t;
}
/** 두 모서리로 잡은 범위를 걸치는 병합까지 품도록 넓힌다(엑셀과 같다). */
export function layoutExpandRange(t, a, b) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    let r1 = Math.max(0, Math.min(a.r, b.r));
    let r2 = Math.min(rows - 1, Math.max(a.r, b.r));
    let c1 = Math.max(0, Math.min(a.c, b.c));
    let c2 = Math.min(cols - 1, Math.max(a.c, b.c));
    for (let guard = 0; guard < rows * cols; guard++) {
        let grew = false;
        for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
                const { r: mr, c: mc } = layoutMasterOf(t, r, c);
                const m = t.cells[mr][mc];
                const mr2 = mr + (m.rowSpan ?? 1) - 1;
                const mc2 = mc + (m.colSpan ?? 1) - 1;
                if (mr < r1)
                    ((r1 = mr), (grew = true));
                if (mc < c1)
                    ((c1 = mc), (grew = true));
                if (mr2 > r2)
                    ((r2 = mr2), (grew = true));
                if (mc2 > c2)
                    ((c2 = mc2), (grew = true));
            }
        }
        if (!grew)
            break;
    }
    return { r1, r2, c1, c2 };
}
/**
 * 칸 병합. 두 모서리로 사각형을 잡고, 그 안에 걸치는 병합이 있으면 사각형을
 * 그만큼 넓힌다(엑셀이 그러듯 — 안 넓히면 병합이 겹친다). 왼쪽 위가 주인이 되고
 * 글자는 주인 것만 남는다.
 */
export function layoutMergeCells(t, a, b) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    let r1 = Math.max(0, Math.min(a.r, b.r));
    let r2 = Math.min(rows - 1, Math.max(a.r, b.r));
    let c1 = Math.max(0, Math.min(a.c, b.c));
    let c2 = Math.min(cols - 1, Math.max(a.c, b.c));
    // 걸치는 병합을 다 품을 때까지 넓힌다.
    for (let guard = 0; guard < rows * cols; guard++) {
        let grew = false;
        for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
                const { r: mr, c: mc } = layoutMasterOf(t, r, c);
                const m = t.cells[mr][mc];
                const mr2 = mr + (m.rowSpan ?? 1) - 1;
                const mc2 = mc + (m.colSpan ?? 1) - 1;
                if (mr < r1)
                    ((r1 = mr), (grew = true));
                if (mc < c1)
                    ((c1 = mc), (grew = true));
                if (mr2 > r2)
                    ((r2 = mr2), (grew = true));
                if (mc2 > c2)
                    ((c2 = mc2), (grew = true));
            }
        }
        if (!grew)
            break;
    }
    if (r1 === r2 && c1 === c2)
        return t;
    const cells = cloneCells(t.cells);
    const master = { ...cells[r1][c1] };
    delete master.covered;
    for (let r = r1; r <= r2; r++) {
        for (let c = c1; c <= c2; c++) {
            if (r === r1 && c === c1)
                continue;
            cells[r][c] = { covered: true };
        }
    }
    master.rowSpan = r2 - r1 + 1;
    master.colSpan = c2 - c1 + 1;
    if (master.rowSpan === 1)
        delete master.rowSpan;
    if (master.colSpan === 1)
        delete master.colSpan;
    cells[r1][c1] = master;
    return { ...t, cells };
}
/** 병합 풀기. 주인 칸을 주면 덮였던 칸이 빈 칸으로 돌아온다. */
export function layoutSplitCell(t, r, c) {
    const { r: mr, c: mc } = layoutMasterOf(t, r, c);
    const m = t.cells[mr]?.[mc];
    if (!m)
        return t;
    const rs = m.rowSpan ?? 1;
    const cs = m.colSpan ?? 1;
    if (rs === 1 && cs === 1)
        return t;
    const cells = cloneCells(t.cells);
    for (let rr = mr; rr < Math.min(mr + rs, cells.length); rr++) {
        for (let cc = mc; cc < Math.min(mc + cs, cells[rr].length); cc++) {
            if (rr === mr && cc === mc)
                continue;
            cells[rr][cc] = { text: '' };
        }
    }
    const master = { ...cells[mr][mc] };
    delete master.rowSpan;
    delete master.colSpan;
    cells[mr][mc] = master;
    return { ...t, cells };
}
/** 표 전체 크기 바꾸기 — 열·행을 비율대로 늘리고 줄인다. */
export function layoutResizeTable(t, w, h) {
    const cols = t.colWidths.length;
    const rows = t.rowHeights.length;
    const minW = MIN_LAYOUT_CELL * cols;
    const minH = MIN_LAYOUT_CELL * rows;
    const W = Math.max(minW, Math.round(w));
    const H = Math.max(minH, Math.round(h));
    const colWidths = scaleTo(t.colWidths, W);
    const rowHeights = scaleTo(t.rowHeights, H);
    return { ...t, colWidths, rowHeights, w: sum(colWidths), h: sum(rowHeights) };
}
/** 한 열의 너비 바꾸기(다른 열은 그대로 → 표 폭이 바뀐다). */
export function layoutSetColWidth(t, c, width) {
    const colWidths = [...t.colWidths];
    colWidths[c] = Math.max(MIN_LAYOUT_CELL, Math.round(width));
    return { ...t, colWidths, w: sum(colWidths) };
}
export function layoutSetRowHeight(t, r, height) {
    const rowHeights = [...t.rowHeights];
    rowHeights[r] = Math.max(MIN_LAYOUT_CELL, Math.round(height));
    return { ...t, rowHeights, h: sum(rowHeights) };
}
function sum(xs) {
    return xs.reduce((a, b) => a + b, 0);
}
/** 항목들을 비율대로 늘려 합이 `total` 이 되게 한다(반올림 오차는 마지막에 몰아 준다). */
function scaleTo(parts, total) {
    const s = sum(parts) || 1;
    const out = parts.map((p) => Math.max(MIN_LAYOUT_CELL, Math.round((p / s) * total)));
    const diff = total - sum(out);
    if (out.length > 0)
        out[out.length - 1] = Math.max(MIN_LAYOUT_CELL, out[out.length - 1] + diff);
    return out;
}
/** 객체·칸이 쓰는 사진 키 전부(중복 없이). 서버가 소유 검사·URL 발급에 쓴다. */
export function layoutImageKeys(objects) {
    const keys = new Set();
    for (const o of objects) {
        if (o.kind === 'image' && o.s3Key)
            keys.add(o.s3Key);
        if (o.kind === 'table') {
            for (const row of o.cells)
                for (const c of row)
                    if (c.image?.s3Key)
                        keys.add(c.image.s3Key);
        }
    }
    return [...keys];
}
/** 사진 키를 바꾼다(서식으로 복사할 때). 없는 키는 그대로. */
export function layoutRemapImageKeys(objects, map) {
    return objects.map((o) => {
        if (o.kind === 'image')
            return map[o.s3Key] ? { ...o, s3Key: map[o.s3Key] } : o;
        if (o.kind === 'table') {
            return {
                ...o,
                cells: o.cells.map((row) => row.map((c) => c.image?.s3Key && map[c.image.s3Key]
                    ? { ...c, image: { ...c.image, s3Key: map[c.image.s3Key] } }
                    : c)),
            };
        }
        return o;
    });
}
// ── 테두리 (엑셀식) ────────────────────────────────────────────────────────
function sideOf(cell, side) {
    if (!cell)
        return undefined;
    return cell.borders?.[side] ?? cell.border;
}
/** 맞닿은 두 변 중 이긴 쪽 — 나중에 정한 것, 같으면 두꺼운 것. */
function pickBorder(a, b) {
    if (!a)
        return b;
    if (!b)
        return a;
    const ta = a.at ?? 0;
    const tb = b.at ?? 0;
    if (ta !== tb)
        return ta > tb ? a : b;
    return b.width > a.width ? b : a;
}
function sameLook(a, b) {
    return (a.width === b.width && a.color.toLowerCase() === b.color.toLowerCase() && a.style === b.style);
}
/**
 * 표에 그을 선 조각들. **화면(SVG)과 PDF 가 같은 함수**를 쓴다 — 칸마다 사각형을
 * 따로 그리면 맞닿은 변이 두 번 그어져 두께가 달라 보이고, 한 칸만 바꿨는데 이웃
 * 변까지 덮였다(2026-09-23 오너 지적). 격자선 조각마다 한 번만, 이긴 값으로 긋는다.
 * 병합 안쪽 선은 긋지 않는다. 같은 모양으로 이어진 조각은 합친다(점선이 끊기지 않게).
 */
export function layoutTableEdges(t) {
    const rows = t.rowHeights.length;
    const cols = t.colWidths.length;
    const xs = [0];
    for (const w of t.colWidths)
        xs.push(xs[xs.length - 1] + w);
    const ys = [0];
    for (const h of t.rowHeights)
        ys.push(ys[ys.length - 1] + h);
    const master = (r, c) => layoutMasterOf(t, r, c);
    const cellAt = (r, c) => {
        const m = master(r, c);
        return { key: `${m.r},${m.c}`, cell: t.cells[m.r]?.[m.c] };
    };
    const out = [];
    const push = (seg) => {
        if (seg.border.width <= 0)
            return;
        const prev = out[out.length - 1];
        if (prev &&
            sameLook(prev.border, seg.border) &&
            ((prev.y1 === prev.y2 && seg.y1 === seg.y2 && prev.y1 === seg.y1 && prev.x2 === seg.x1) ||
                (prev.x1 === prev.x2 && seg.x1 === seg.x2 && prev.x1 === seg.x1 && prev.y2 === seg.y1))) {
            prev.x2 = seg.x2;
            prev.y2 = seg.y2;
            return;
        }
        out.push({ ...seg });
    };
    // 가로선 — i 번째 줄 위쪽 선(i = rows 는 맨 아래).
    for (let i = 0; i <= rows; i++) {
        for (let c = 0; c < cols; c++) {
            const above = i > 0 ? cellAt(i - 1, c) : null;
            const below = i < rows ? cellAt(i, c) : null;
            if (above && below && above.key === below.key)
                continue; // 병합 안쪽
            const b = pickBorder(sideOf(above?.cell, 'bottom'), sideOf(below?.cell, 'top')) ?? t.border;
            push({ x1: xs[c], y1: ys[i], x2: xs[c + 1], y2: ys[i], border: b });
        }
    }
    // 세로선 — j 번째 열 왼쪽 선.
    for (let j = 0; j <= cols; j++) {
        for (let r = 0; r < rows; r++) {
            const left = j > 0 ? cellAt(r, j - 1) : null;
            const right = j < cols ? cellAt(r, j) : null;
            if (left && right && left.key === right.key)
                continue;
            const b = pickBorder(sideOf(left?.cell, 'right'), sideOf(right?.cell, 'left')) ?? t.border;
            push({ x1: xs[j], y1: ys[r], x2: xs[j], y2: ys[r + 1], border: b });
        }
    }
    return out;
}
/**
 * 고른 범위에 테두리를 입힌다 — 엑셀 테두리 메뉴(모든/바깥쪽/안쪽/위/아래/왼쪽/
 * 오른쪽/없음)와 같다. 펜(두께·모양·색)은 호출하는 쪽이 정한다. `reset` 은 범위의
 * 변 설정을 지워 표 기본으로 되돌린다.
 *
 * 범위 **안쪽 칸의 변만** 바꾼다. 바깥 이웃 칸은 건드리지 않고, 새로 정한 변이
 * `at` 으로 이긴다 — 이웃이 범위 밖까지 병합돼 있어도 범위 밖 선이 바뀌지 않는다.
 */
export function layoutApplyBorders(t, a, b, mode, pen, now = Date.now()) {
    const { r1, r2, c1, c2 } = layoutExpandRange(t, a, b);
    const cells = t.cells.map((row) => row.map((c) => ({ ...c, ...(c.borders ? { borders: { ...c.borders } } : {}) })));
    const value = mode === 'reset'
        ? undefined
        : { width: mode === 'none' ? 0 : pen.width, color: pen.color, style: pen.style, at: now };
    const seen = new Set();
    for (let r = r1; r <= r2; r++) {
        for (let c = c1; c <= c2; c++) {
            const m = layoutMasterOf(t, r, c);
            const key = `${m.r},${m.c}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            const cell = cells[m.r][m.c];
            const mr2 = m.r + (cell.rowSpan ?? 1) - 1;
            const mc2 = m.c + (cell.colSpan ?? 1) - 1;
            const boundary = {
                top: m.r === r1,
                bottom: mr2 === r2,
                left: m.c === c1,
                right: mc2 === c2,
            };
            for (const side of ['top', 'right', 'bottom', 'left']) {
                let apply = false;
                switch (mode) {
                    case 'all':
                    case 'none':
                    case 'reset':
                        apply = true;
                        break;
                    case 'outer':
                        apply = boundary[side];
                        break;
                    case 'inner':
                        apply = !boundary[side];
                        break;
                    default:
                        apply = mode === side && boundary[side];
                }
                if (!apply)
                    continue;
                const next = { ...(cell.borders ?? {}) };
                if (value)
                    next[side] = value;
                else
                    delete next[side];
                if (Object.keys(next).length > 0)
                    cell.borders = next;
                else
                    delete cell.borders;
                // 옛 칸 통째 테두리는 새 변 설정과 섞이면 헷갈린다 — 되돌리기면 같이 지운다.
                if (mode === 'reset')
                    delete cell.border;
            }
        }
    }
    return { ...t, cells };
}
/**
 * 표에서 **가장 길게 그어진 선**의 두께·모양·색(`at` 없이). 그어진 선이 없으면 null.
 * 화면·PDF 와 같은 선 조각(`layoutTableEdges`)을 길이로 센다 — 병합 안쪽처럼 안 보이는
 * 변은 세지 않는다.
 */
export function layoutDominantBorder(t) {
    const tally = new Map();
    for (const e of layoutTableEdges(t)) {
        if (!(e.border.width > 0))
            continue;
        const color = e.border.color.toLowerCase();
        const key = `${e.border.width}|${color}|${e.border.style}`;
        const len = Math.abs(e.x2 - e.x1) + Math.abs(e.y2 - e.y1);
        const cur = tally.get(key);
        if (cur)
            cur.len += len;
        else
            tally.set(key, { b: { width: e.border.width, color, style: e.border.style }, len });
    }
    let best = null;
    for (const v of tally.values())
        if (!best || v.len > best.len)
            best = v;
    return best ? best.b : null;
}
/**
 * 테두리 펜의 지금 값 — 저장된 펜 → 표에 그어진 가장 흔한 선 → 표 기본선(두께가 있으면)
 * → 기본값 순. 엑셀로 가져온 표는 기본선이 0 이라, 예전엔 펜이 늘 검정 1pt 로 떴다.
 */
export function layoutTablePen(t) {
    const strip = (b) => ({
        width: b.width,
        color: b.color,
        style: b.style,
    });
    if (t.pen)
        return strip(t.pen);
    const d = layoutDominantBorder(t);
    if (d)
        return d;
    if (t.border.width > 0)
        return strip(t.border);
    return strip(DEFAULT_LAYOUT_BORDER);
}
/** 캔버스 한 점이 떨어진 표 칸(병합이면 주인). 위에 그려진 표가 먼저. 없으면 null. */
export function layoutHitCell(objects, x, y) {
    for (let i = objects.length - 1; i >= 0; i--) {
        const o = objects[i];
        if (o.kind !== 'table')
            continue;
        if (x < o.x || y < o.y || x >= o.x + o.w || y >= o.y + o.h)
            continue;
        let c = 0;
        let acc = o.x;
        while (c < o.colWidths.length - 1 && x >= acc + o.colWidths[c])
            acc += o.colWidths[c++];
        let r = 0;
        acc = o.y;
        while (r < o.rowHeights.length - 1 && y >= acc + o.rowHeights[r])
            acc += o.rowHeights[r++];
        const m = layoutMasterOf(o, r, c);
        return { tableId: o.id, r: m.r, c: m.c };
    }
    return null;
}
// ── 자동 필드 ─────────────────────────────────────────────────────────────
/**
 * 객체가 만든 필드임을 표시하는 접두사.
 *
 * 저장할 때 **객체가 만든 필드만 갈아 끼우고 사람이 드래그로 찍은 필드는
 * 남긴다.** 구별이 없으면 재빌드가 손으로 놓은 필드를 쓸어 버린다.
 */
export const LAYOUT_FIELD_ID_PREFIX = 'lay_';
export function isLayoutGeneratedField(fieldId) {
    return fieldId.startsWith(LAYOUT_FIELD_ID_PREFIX);
}
/** 입력 칸 안쪽 여백(px) — 칸 테두리와 필드 상자가 겹쳐 보이지 않게. */
const CELL_FIELD_INSET = 2;
/**
 * 표 칸의 입력 칸에서 `SignatureField` 를 만든다. **서버와 화면이 같은 함수**를
 * 쓴다 — 화면은 미리 보여 주고, 서버는 저장할 때 정본으로 다시 만든다.
 * id 는 표 id + 행 + 열이라 같은 칸은 늘 같은 id 다(다시 저장해도 갈아 끼우기
 * 가 제자리에 된다).
 */
export function layoutAutoFields(objects) {
    const out = [];
    for (const o of objects) {
        if (o.kind !== 'table')
            continue;
        for (let r = 0; r < o.cells.length; r++) {
            for (let c = 0; c < o.cells[r].length; c++) {
                const cell = o.cells[r][c];
                if (cell.covered || !cell.field)
                    continue;
                const rect = layoutCellRect(o, r, c);
                const f = cell.field;
                const x = Math.round(rect.x + CELL_FIELD_INSET);
                const y = Math.round(rect.y + CELL_FIELD_INSET);
                out.push({
                    id: `${LAYOUT_FIELD_ID_PREFIX}${o.id}_${r}_${c}`,
                    pageIndex: Math.floor(y / LAYOUT_PAGE_H),
                    x,
                    y,
                    w: Math.max(1, Math.round(rect.w - CELL_FIELD_INSET * 2)),
                    h: Math.max(1, Math.round(rect.h - CELL_FIELD_INSET * 2)),
                    type: f.type,
                    signerSlot: f.slot,
                    binding: f.binding ?? { kind: 'literal' },
                    required: !!f.required,
                    defaultValue: null,
                    placeholder: f.placeholder ?? (cell.text?.trim() || null),
                    ...(f.type === 'dropdown' ? { options: f.options ?? [] } : {}),
                    align: cell.align ?? DEFAULT_LAYOUT_TEXT_ALIGN,
                    // 2026-10-20 — 칸 글자 크기 그대로(글자 칸과 같은 크기로 보이게).
                    fontSize: cell.fontSize ?? o.fontSize,
                });
            }
        }
    }
    return out;
}
// ── 검사 ──────────────────────────────────────────────────────────────────
function isHex(s) {
    return typeof s === 'string' && /^#[0-9a-fA-F]{6}$/.test(s);
}
function checkBorder(b, where, problems) {
    const x = b;
    if (!x || typeof x !== 'object') {
        problems.push(`${where}: 테두리 정보가 없습니다.`);
        return;
    }
    if (typeof x.width !== 'number' || x.width < 0 || x.width > 20) {
        problems.push(`${where}: 테두리 두께가 올바르지 않습니다.`);
    }
    if (!isHex(x.color))
        problems.push(`${where}: 테두리 색이 올바르지 않습니다.`);
    if (x.style !== 'solid' && x.style !== 'dashed') {
        problems.push(`${where}: 테두리 모양이 올바르지 않습니다.`);
    }
}
/**
 * 레이아웃이 성립하는지 본다. **서버와 화면이 같은 규칙을 쓴다** — 화면에서만
 * 막으면 API 로 직접 부르는 경로가 남고, 서버에서만 막으면 사람이 저장 버튼을
 * 누른 뒤에야 알게 된다. 돌려주는 값은 사람이 읽을 사유다(빈 배열 = 문제 없음).
 */
export function validateTemplateLayout(layout) {
    if (!layout || typeof layout !== 'object')
        return ['레이아웃이 객체가 아닙니다.'];
    const L = layout;
    if (!Array.isArray(L.objects))
        return ['객체 목록이 배열이 아닙니다.'];
    if (L.objects.length > MAX_LAYOUT_OBJECTS) {
        return [`객체가 너무 많습니다(${L.objects.length} > ${MAX_LAYOUT_OBJECTS}).`];
    }
    if (L.basePdfS3Key !== null &&
        L.basePdfS3Key !== undefined &&
        typeof L.basePdfS3Key !== 'string') {
        return ['밑에 깔 PDF 키가 올바르지 않습니다.'];
    }
    if (L.pages !== undefined) {
        if (!Array.isArray(L.pages) || L.pages.length === 0 || L.pages.length > 50) {
            return ['장 구성이 올바르지 않습니다(1~50장).'];
        }
        if (!L.pages.every((pg) => pg === null || (typeof pg === 'number' && Number.isInteger(pg) && pg >= 0))) {
            return ['장 구성에 모르는 값이 있습니다.'];
        }
    }
    const problems = [];
    const seen = new Set();
    L.objects.forEach((raw, i) => {
        const at = `${i + 1}번째 객체`;
        if (!raw || typeof raw !== 'object') {
            problems.push(`${at}: 객체가 아닙니다.`);
            return;
        }
        const o = raw;
        if (typeof o.id !== 'string' || !o.id.trim())
            problems.push(`${at}: id 가 없습니다.`);
        else if (seen.has(o.id))
            problems.push(`${at}: id 가 중복입니다(${o.id}).`);
        else
            seen.add(o.id);
        for (const k of ['x', 'y', 'w', 'h']) {
            const v = o[k];
            if (typeof v !== 'number' || !Number.isFinite(v))
                problems.push(`${at}: ${k} 가 숫자가 아닙니다.`);
        }
        if (typeof o.x === 'number' &&
            typeof o.w === 'number' &&
            (o.x < 0 || o.x + o.w > LAYOUT_PAGE_W + 1)) {
            problems.push(`${at}: 종이 폭을 벗어났습니다.`);
        }
        if (typeof o.y === 'number' && o.y < 0)
            problems.push(`${at}: y 가 음수입니다.`);
        if ((typeof o.w === 'number' && o.w < 1) || (typeof o.h === 'number' && o.h < 1)) {
            problems.push(`${at}: 크기가 0 입니다.`);
        }
        switch (o.kind) {
            case 'table': {
                const t = o;
                const rows = t.rowHeights?.length ?? 0;
                const cols = t.colWidths?.length ?? 0;
                if (!Array.isArray(t.rowHeights) || rows === 0)
                    problems.push(`${at}(표): 행이 없습니다.`);
                if (!Array.isArray(t.colWidths) || cols === 0)
                    problems.push(`${at}(표): 열이 없습니다.`);
                if (rows > MAX_LAYOUT_TABLE_ROWS)
                    problems.push(`${at}(표): 행이 너무 많습니다(${rows}).`);
                if (cols > MAX_LAYOUT_TABLE_COLS)
                    problems.push(`${at}(표): 열이 너무 많습니다(${cols}).`);
                if (!Array.isArray(t.cells) || t.cells.length !== rows) {
                    problems.push(`${at}(표): 칸 격자가 행 수와 맞지 않습니다.`);
                    break;
                }
                if (typeof t.fontSize !== 'number' || t.fontSize < 6 || t.fontSize > 72) {
                    problems.push(`${at}(표): 글자 크기가 올바르지 않습니다.`);
                }
                checkBorder(t.border, `${at}(표)`, problems);
                if (t.pen !== undefined)
                    checkBorder(t.pen, `${at}(표 테두리 펜)`, problems);
                t.cells.forEach((row, r) => {
                    if (!Array.isArray(row) || row.length !== cols) {
                        problems.push(`${at}(표) ${r + 1}행: 칸 수가 열 수와 맞지 않습니다.`);
                        return;
                    }
                    row.forEach((cell, c) => {
                        const where = `${at}(표) ${r + 1}행 ${c + 1}칸`;
                        if (!cell || typeof cell !== 'object') {
                            problems.push(`${where}: 칸이 객체가 아닙니다.`);
                            return;
                        }
                        if (cell.covered)
                            return;
                        const rs = cell.rowSpan ?? 1;
                        const cs = cell.colSpan ?? 1;
                        if (r + rs > rows || c + cs > cols)
                            problems.push(`${where}: 병합이 표 밖으로 나갑니다.`);
                        if (cell.fill !== undefined && cell.fill !== null && !isHex(cell.fill)) {
                            problems.push(`${where}: 음영 색이 올바르지 않습니다.`);
                        }
                        if (cell.color !== undefined && !isHex(cell.color)) {
                            problems.push(`${where}: 글자 색이 올바르지 않습니다.`);
                        }
                        if (cell.border !== undefined)
                            checkBorder(cell.border, where, problems);
                        if (cell.borders !== undefined) {
                            if (!cell.borders || typeof cell.borders !== 'object') {
                                problems.push(`${where}: 변 테두리 정보가 올바르지 않습니다.`);
                            }
                            else {
                                for (const [side, bd] of Object.entries(cell.borders)) {
                                    if (!['top', 'right', 'bottom', 'left'].includes(side)) {
                                        problems.push(`${where}: 모르는 변입니다(${side}).`);
                                        continue;
                                    }
                                    checkBorder(bd, where, problems);
                                }
                            }
                        }
                        if (cell.image !== undefined && cell.image !== null) {
                            if (typeof cell.image.s3Key !== 'string' || !cell.image.s3Key.trim()) {
                                problems.push(`${where}: 사진 파일이 없습니다.`);
                            }
                            const im = cell.image;
                            if (im.fit !== undefined && !['contain', 'cover', 'stretch'].includes(im.fit)) {
                                problems.push(`${where}: 사진 맞춤 방식이 올바르지 않습니다.`);
                            }
                            if (im.padding !== undefined &&
                                (typeof im.padding !== 'number' || im.padding < 0 || im.padding > 40)) {
                                problems.push(`${where}: 사진 여백이 올바르지 않습니다(0~40).`);
                            }
                            if (im.opacity !== undefined &&
                                (typeof im.opacity !== 'number' || im.opacity < 0.05 || im.opacity > 1)) {
                                problems.push(`${where}: 사진 불투명도가 올바르지 않습니다.`);
                            }
                        }
                        if (cell.fontSize !== undefined &&
                            (typeof cell.fontSize !== 'number' || cell.fontSize < 6 || cell.fontSize > 72)) {
                            problems.push(`${where}: 글자 크기가 올바르지 않습니다.`);
                        }
                        const f = cell.field;
                        if (!f)
                            return;
                        if (!LAYOUT_CELL_FIELD_TYPES.includes(f.type)) {
                            problems.push(`${where}: 모르는 필드 종류입니다(${String(f.type)}).`);
                        }
                        if (f.type === 'dropdown' && (!Array.isArray(f.options) || f.options.length === 0)) {
                            problems.push(`${where}: 드롭다운인데 선택지가 없습니다.`);
                        }
                        // 슬롯도 없고 자동 채움도 없으면 **아무도 채울 수 없는 칸**이다.
                        const bound = f.binding && f.binding.kind !== 'literal';
                        if (!f.slot && !bound) {
                            problems.push(`${where}: 채울 사람(서명자)도 자동 채움도 지정되지 않았습니다.`);
                        }
                    });
                });
                break;
            }
            case 'text': {
                const x = o;
                if (typeof x.text !== 'string')
                    problems.push(`${at}(텍스트): 내용이 없습니다.`);
                if (typeof x.fontSize !== 'number' || x.fontSize < 6 || x.fontSize > 120) {
                    problems.push(`${at}(텍스트): 글자 크기가 올바르지 않습니다.`);
                }
                if (x.color !== undefined && !isHex(x.color))
                    problems.push(`${at}(텍스트): 글자 색이 올바르지 않습니다.`);
                break;
            }
            case 'shape': {
                const s = o;
                if (s.shape !== 'rect' && s.shape !== 'ellipse' && s.shape !== 'line') {
                    problems.push(`${at}(도형): 모르는 도형입니다(${String(s.shape)}).`);
                }
                checkBorder(s.stroke, `${at}(도형)`, problems);
                if (s.fill !== undefined && s.fill !== null && !isHex(s.fill)) {
                    problems.push(`${at}(도형): 채움 색이 올바르지 않습니다.`);
                }
                if (s.line !== undefined) {
                    const L = s.line;
                    for (const k of ['x1', 'y1', 'x2', 'y2']) {
                        if (typeof L[k] !== 'number' || !Number.isFinite(L[k]))
                            problems.push(`${at}(도형): 선 끝점이 올바르지 않습니다.`);
                    }
                }
                break;
            }
            case 'image': {
                const im = o;
                if (typeof im.s3Key !== 'string' || !im.s3Key.trim())
                    problems.push(`${at}(사진): 파일이 없습니다.`);
                break;
            }
            default:
                problems.push(`${at}: 모르는 객체 종류입니다(${String(o.kind)}).`);
        }
    });
    return problems;
}
/** 서식 사진이 사는 곳. `buildLayout` 이 이 접두사의 키를 허용한다. */
export const LAYOUT_PRESET_ASSET_PREFIX = 'signature-layout-presets/';
// ── 사진 라이브러리 — 팔레트 '사진' 에서 고르는 저장된 사진 ─────────────────
// (오너 요청 2026-09-22). 불러온 사진은 자동으로 쌓이고, 사용자가 지울 때만 사라진다.
/** 라이브러리 사진이 사는 곳. 양식·서식 어디서든 참조하므로 `buildLayout` 이 허용한다. */
export const LAYOUT_LIBRARY_ASSET_PREFIX = 'signature-layout-assets/';
//# sourceMappingURL=signature-layout.js.map